import { ColumnDef } from "@tanstack/react-table"
import { sortableColumnHeader } from "@/components/data-table"
import { useBillingTranslation } from "@/i18n/hooks"
import { Link } from "@/components/tailwind/link"
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { TrashIcon } from '@heroicons/react/16/solid'
import { BillingRunSummary } from "@/services/billing-services"
import { formatDateString } from "@/lib/utils"
import { getProgressText, getProgressColor } from "@/utils/billing-progress"

export interface BillingRunWithProgress extends BillingRunSummary {
  progress: number
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

function ProgressBar({ progress }: { progress: number }) {
  const { t } = useBillingTranslation()
  const progressColor = getProgressColor(progress)
  const progressText = getProgressText(progress, t)

  return (
    <div className="w-full">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-gray-400">{progressText}</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`${progressColor} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

function ProgressCell({ progress }: { progress: number }) {
  return (
    <div className="w-64">
      <ProgressBar progress={progress} />
    </div>
  )
}

function ActionsHeader() {
  const { t } = useBillingTranslation()
  return <div className="text-right">{t('table.headers.actions')}</div>
}

export function createBillingRunColumns(onDelete: (id: string) => void): ColumnDef<BillingRunWithProgress>[] {
  return [
    {
      accessorKey: "progress",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.progress', 'billing'),
      cell: ({ row }) => <ProgressCell progress={row.original.progress} />,
      sortingFn: (rowA, rowB) => rowA.original.progress - rowB.original.progress,
    },
    {
      accessorKey: "membershipFeeCount",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.membershipFees', 'billing'),
      cell: ({ row }) => row.original.membershipFeeCount,
    },
    {
      accessorKey: "oneTimeDonationCount",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.oneTimeDonations', 'billing'),
      cell: ({ row }) => row.original.oneTimeDonationCount,
    },
    {
      accessorKey: "annualDonationCount",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.annualDonations', 'billing'),
      cell: ({ row }) => row.original.annualDonationCount,
    },
    {
      accessorKey: "totalAmount",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.totalAmount', 'billing'),
      cell: ({ row }) => formatAmount(row.original.totalAmount),
      sortingFn: (rowA, rowB) => rowA.original.totalAmount - rowB.original.totalAmount,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.createdAt', 'billing'),
      cell: ({ row }) => formatDateString(row.original.createdAt),
      sortingFn: (rowA, rowB) => new Date(rowA.original.createdAt).getTime() - new Date(rowB.original.createdAt).getTime(),
    },
    {
      id: "actions",
      header: () => <ActionsHeader />,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Link href={`/finance/billing/runs/${row.original.id}`} className="inline-flex items-center">
            <InformationCircleIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onDelete(row.original.id)}
            className="inline-flex items-center text-gray-400 hover:text-red-600 dark:hover:text-red-400"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]
}